import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useProducts } from "../context/ProductContext";
import { useWishlist } from "../context/WishlistContext";
import { useCart } from "../context/CartContext";
import ImageGallery from "../components/ImageGallery";
import AccountDetailsPopup from "../components/AccountDetailsPopup";

const ProductDetail = () => {
  const { id } = useParams();
  const { products, categories, isLoading } = useProducts();
  const product = products.find((p) => String(p.id) === String(id));
  const [selectedVariant, setSelectedVariant] = useState(null);
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();
  const { addToCart, cart, updateQuantity } = useCart();
  const inWishlist = product ? isInWishlist(product.id) : false;
  const [showAccountPopup, setShowAccountPopup] = useState(false);

  // Set selected variant when product loads
  useEffect(() => {
    if (product?.variants?.length > 0) {
      setSelectedVariant(product.variants[0]);
    }
  }, [product]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  // Get the quantity of the selected variant in cart
  const cartItem = cart.find(
    (item) =>
      item.id === product?.id && item.variant.size === selectedVariant?.size,
  );
  const cartQuantity = cartItem ? cartItem.quantity : 0;

  const handleWishlistToggle = () => {
    if (inWishlist) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product.id);
    }
  };

  const [isAnimating, setIsAnimating] = useState(false);

  const handleAddToCart = () => {
    if (selectedVariant) {
      addToCart(product, selectedVariant, 1);
      triggerAnimation();
    }
  };

  const handleIncreaseQuantity = () => {
    if (selectedVariant) {
      updateQuantity(product.id, selectedVariant.size, cartQuantity + 1);
      triggerAnimation();
    }
  };

  const handleDecreaseQuantity = () => {
    if (selectedVariant) {
      updateQuantity(product.id, selectedVariant.size, cartQuantity - 1);
      triggerAnimation();
    }
  };

  const triggerAnimation = () => {
    setIsAnimating(true);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleWhatsAppOrder = () => {
    if (!selectedVariant) return;

    // Check if account details exist
    const accountDetails = localStorage.getItem("accountDetails");
    if (!accountDetails) {
      setShowAccountPopup(true);
      return;
    }

    const account = JSON.parse(accountDetails);
    const quantity = cartQuantity > 0 ? cartQuantity : 1;
    const totalPrice = selectedVariant.price * quantity;

    let message = `🛒 *NEW ORDER REQUEST*\n\n`;
    message += `📦 *Product Details:*\n`;
    message += `─────────────────\n`;
    message += `${product.name}\n`;
    message += `📏 Size: ${selectedVariant.size}\n`;
    message += `🔢 Quantity: ${quantity}\n`;
    message += `💰 Price per unit: ₹${selectedVariant.price}\n`;
    message += `─────────────────\n`;
    message += `💵 *Total: ₹${totalPrice}*\n\n`;

    message += `📍 *Delivery Details:*\n`;
    message += `─────────────────\n`;
    message += `👤 Name: ${account.name || "N/A"}\n`;
    message += `📱 Mobile: ${account.mobile}\n`;
    message += `📧 Email: ${account.email || "N/A"}\n`;
    message += `🏠 Address:\n`;
    message += `   ${account.addressLine1}\n`;
    if (account.addressLine2) message += `   ${account.addressLine2}\n`;
    if (account.addressLine3) message += `   ${account.addressLine3}\n`;
    message += `   ${account.city}, ${account.state} - ${account.pincode}\n\n`;
    message += `*Additional shipping charges applicable.*\n`;

    const whatsappUrl = `https://wa.me/919840453354?text=${encodeURIComponent(
      message,
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  if (isLoading && !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="text-gray-600 mt-3">Loading product...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Product not found
          </h2>
          <Link to="/" className="text-green-600 hover:underline">
            Go back to home
          </Link>
        </div>
      </div>
    );
  }

  const currentPrice = selectedVariant?.price || product.price;

  return (
    <div className="min-h-screen bg-gray-50 py-3 md:py-6 page-transition">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Breadcrumb Navigation */}
        <nav className="mb-4 text-xs md:text-sm">
          <ol className="flex items-center space-x-2 text-gray-600">
            <li>
              <Link to="/" className="hover:text-green-600 transition">
                Home
              </Link>
            </li>
            <li>
              <span className="text-gray-400">›</span>
            </li>
            <li>
              <Link
                to="/products"
                state={{ category: product.category }}
                className="hover:text-green-600 transition"
              >
                {categories.find((c) => c.id === product.category)?.name ||
                  "Products"}
              </Link>
            </li>
            <li>
              <span className="text-gray-400">›</span>
            </li>
            <li className="text-gray-800 font-medium truncate max-w-xs">
              {product.name}
            </li>
          </ol>
        </nav>

        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="grid md:grid-cols-[480px_1fr] gap-4 md:gap-6 p-4 md:p-6">
            {/* Left Column - Image Only */}
            <div>
              <div className="relative">
                <ImageGallery
                  images={product.images || [product.image]}
                  productName={product.name}
                />
              </div>
            </div>

            {/* Right Column - Product Details and Buttons */}
            <div className="flex flex-col">
              <h1 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-800 mb-2">
                {product.name}
              </h1>

              <div className="flex items-center mb-3">
                <div className="flex items-center bg-green-500 text-white px-2 py-1 rounded text-xs">
                  <span className="font-semibold">{product.rating}</span>
                  <span className="ml-1">★</span>
                </div>
                <span className="ml-2 text-gray-600 text-xs">
                  Excellent Rating
                </span>
              </div>

              <div className="mb-3">
                <div className="flex flex-wrap items-baseline mb-1 gap-2">
                  <span className="text-xl md:text-2xl font-bold text-green-600">
                    ₹{currentPrice}
                  </span>
                </div>
                <p className="text-xs text-gray-600">Inclusive of all taxes</p>
                {/* <p className="text-xs text-red-500 mt-1">Additional shipping charges applicable.</p> */}
              </div>

              <div className="mb-3">
                <h3 className="font-semibold text-gray-800 mb-2 text-xs md:text-sm">
                  Select Variant:
                </h3>
                <div className="flex flex-wrap gap-2">
                  {product.variants.map((variant) => (
                    <button
                      key={variant.size}
                      onClick={() => setSelectedVariant(variant)}
                      className={`px-3 py-2 rounded-lg border-2 font-medium transition text-xs ${
                        selectedVariant?.size === variant.size
                          ? "border-green-500 bg-green-50 text-green-600"
                          : "border-gray-300 hover:border-green-300"
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <span>{variant.size}</span>
                        <span className="text-xs mt-1">₹{variant.price}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-3">
                <h3 className="font-semibold text-gray-800 mb-1 text-xs md:text-sm">
                  Description:
                </h3>
                <p className="text-gray-700 leading-relaxed text-xs md:text-sm">
                  {product.description}
                </p>
              </div>

              <div className="mb-3">
                <h3 className="font-semibold text-gray-800 mb-1 text-xs md:text-sm">
                  Product Details:
                </h3>
                <p className="text-gray-700 leading-relaxed text-xs md:text-sm">
                  {product.details}
                </p>
              </div>

              <div className="mb-3">
                <div className="flex items-center">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      product.inStock
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {product.inStock ? "✓ In Stock" : "✗ Out of Stock"}
                  </span>
                </div>
              </div>

              {/* Action Buttons - Below Product Details */}
              <div className="flex flex-col gap-2 mt-auto max-w-md">
                <div className="flex gap-2">
                  <button
                    onClick={handleWishlistToggle}
                    className={`px-3 py-2 rounded-lg font-semibold transition text-xs md:text-sm border-2 ${
                      inWishlist
                        ? "bg-red-500 text-white border-red-500 hover:bg-red-600"
                        : "bg-white text-red-500 border-red-500 hover:bg-red-50"
                    }`}
                  >
                    {inWishlist ? "❤️" : "🤍"}
                  </button>
                  {!product.inStock ? (
                    <button
                      disabled
                      className="flex-1 border-2 border-gray-300 text-gray-400 py-2 rounded-lg font-semibold text-xs md:text-sm cursor-not-allowed"
                    >
                      Out of Stock
                    </button>
                  ) : cartQuantity === 0 ? (
                    <button
                      onClick={handleAddToCart}
                      className={`flex-1 border-2 border-green-500 text-green-600 py-2 rounded-lg font-semibold hover:bg-green-50 transition text-xs md:text-sm button-press ripple ${
                        isAnimating ? "animate-add-to-cart" : ""
                      }`}
                    >
                      Add to Cart
                    </button>
                  ) : (
                    <div
                      className={`flex-1 border-2 border-green-500 text-green-600 rounded-full flex items-center justify-between overflow-hidden h-[36px] md:h-[40px] animate-scale-in ${
                        isAnimating ? "animate-add-to-cart" : ""
                      }`}
                    >
                      <button
                        onClick={handleDecreaseQuantity}
                        className="px-3 h-full hover:bg-green-50 flex items-center justify-center button-press transition-smooth"
                        title={
                          cartQuantity === 1
                            ? "Remove from cart"
                            : "Decrease quantity"
                        }
                      >
                        {cartQuantity === 1 ? (
                          <svg
                            className="w-4 h-4 text-gray-700"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                        ) : (
                          <span className="text-lg font-bold">−</span>
                        )}
                      </button>
                      <span
                        className={`flex-1 text-center text-sm font-semibold ${
                          isAnimating ? "quantity-change" : ""
                        }`}
                      >
                        {cartQuantity}
                      </span>
                      <button
                        onClick={handleIncreaseQuantity}
                        className="px-3 h-full hover:bg-green-50 text-lg font-bold flex items-center justify-center button-press transition-smooth"
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>
                <button
                  onClick={handleWhatsAppOrder}
                  disabled={!product.inStock}
                  className={`w-full py-2 rounded-lg font-semibold transition text-xs md:text-sm flex items-center justify-center gap-2 ${
                    product.inStock
                      ? "bg-green-500 text-white hover:bg-green-600"
                      : "bg-gray-300 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                  </svg>
                  Order via WhatsApp
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Why Choose Us?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-4xl mb-2">🚚</div>
              <h3 className="font-semibold mb-1">Fast Delivery</h3>
              <p className="text-sm text-gray-600">
                Get your order within 24-48 hours
              </p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-2">✓</div>
              <h3 className="font-semibold mb-1">Quality Assured</h3>
              <p className="text-sm text-gray-600">100% authentic products</p>
            </div>
            <div className="text-center">
              <div className="text-4xl mb-2">💰</div>
              <h3 className="font-semibold mb-1">Best Prices</h3>
              <p className="text-sm text-gray-600">
                Competitive pricing with great offers
              </p>
            </div>
          </div>
        </div>
      </div>
      <AccountDetailsPopup
        isOpen={showAccountPopup}
        onClose={() => setShowAccountPopup(false)}
      />
    </div>
  );
};

export default ProductDetail;
